import type { KnowledgeGraphList, KnowledgeGraphVisualization } from "@/types/knowledgeGraph";

/**
 * Mock data for organic chemistry knowledge graph
 * Based on the seed data from backend/src/scripts/seedOrganicChemistry.ts
 */

// Generate consistent UUIDs for mock data
const generateMockId = (seed: string): string => {
	// Simple hash function to generate consistent UUIDs
	let hash = 0;
	for (let i = 0; i < seed.length; i++) {
		const char = seed.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash; // Convert to 32bit integer
	}
	// Convert to UUID-like string (simplified)
	const hex = Math.abs(hash).toString(16).padStart(8, "0");
	return `${hex}-0000-4000-8000-${hex.padEnd(12, "0")}`;
};

export const getMockKnowledgeGraphs = (): KnowledgeGraphList => ({
	count: 1,
	items: [
		{
			id: generateMockId("organic-chemistry-graph"),
			name: "Organic Chemistry Compounds",
			description:
				"A knowledge graph of organic chemical compounds, their properties, functional groups, and relationships",
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
		},
	],
});

export const getMockKnowledgeGraphVisualization = (): KnowledgeGraphVisualization => {
	const graphId = generateMockId("organic-chemistry-graph");
	const now = new Date().toISOString();

	// Node IDs
	const nodeIds = {
		// Compounds
		methane: generateMockId("methane"),
		ethane: generateMockId("ethane"),
		propane: generateMockId("propane"),
		butane: generateMockId("butane"),
		methanol: generateMockId("methanol"),
		ethanol: generateMockId("ethanol"),
		aceticAcid: generateMockId("acetic-acid"),
		formaldehyde: generateMockId("formaldehyde"),
		acetone: generateMockId("acetone"),
		benzene: generateMockId("benzene"),
		toluene: generateMockId("toluene"),
		phenol: generateMockId("phenol"),
		aniline: generateMockId("aniline"),
		ethene: generateMockId("ethene"),
		ethyne: generateMockId("ethyne"),
		// Functional Groups
		hydroxyl: generateMockId("hydroxyl"),
		carbonyl: generateMockId("carbonyl"),
		carboxyl: generateMockId("carboxyl"),
		amino: generateMockId("amino"),
		methyl: generateMockId("methyl"),
		ethyl: generateMockId("ethyl"),
		benzyl: generateMockId("benzyl"),
		// Properties
		flammable: generateMockId("flammable"),
		polar: generateMockId("polar"),
		volatile: generateMockId("volatile"),
		soluble: generateMockId("soluble"),
		// Predicates
		hasFunctionalGroup: generateMockId("hasFunctionalGroup"),
		hasProperty: generateMockId("hasProperty"),
		reactsWith: generateMockId("reactsWith"),
		derivedFrom: generateMockId("derivedFrom"),
	};

	// Graph Element IDs
	const graphElementIds = Object.fromEntries(
		Object.entries(nodeIds).map(([key]) => [key, generateMockId(`graph-element-${key}`)]),
	);

	const nodes = [
		// Compounds
		{
			id: nodeIds.methane,
			graphElementId: graphElementIds.methane,
			name: "Methane",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "CH₄",
				molecularWeight: 16.04,
				category: "alkane",
				description: "Simplest alkane, main component of natural gas",
			},
		},
		{
			id: nodeIds.ethane,
			graphElementId: graphElementIds.ethane,
			name: "Ethane",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "C₂H₆",
				molecularWeight: 30.07,
				category: "alkane",
				description: "Second simplest alkane",
			},
		},
		{
			id: nodeIds.propane,
			graphElementId: graphElementIds.propane,
			name: "Propane",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "C₃H₈",
				molecularWeight: 44.1,
				category: "alkane",
				description: "Common fuel gas",
			},
		},
		{
			id: nodeIds.butane,
			graphElementId: graphElementIds.butane,
			name: "Butane",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "C₄H₁₀",
				molecularWeight: 58.12,
				category: "alkane",
				description: "Lighter fluid component",
			},
		},
		{
			id: nodeIds.methanol,
			graphElementId: graphElementIds.methanol,
			name: "Methanol",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "CH₃OH",
				molecularWeight: 32.04,
				category: "alcohol",
				description: "Simplest alcohol, wood alcohol",
			},
		},
		{
			id: nodeIds.ethanol,
			graphElementId: graphElementIds.ethanol,
			name: "Ethanol",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "C₂H₅OH",
				molecularWeight: 46.07,
				category: "alcohol",
				description: "Drinking alcohol, biofuel",
			},
		},
		{
			id: nodeIds.phenol,
			graphElementId: graphElementIds.phenol,
			name: "Phenol",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "C₆H₅OH",
				molecularWeight: 94.11,
				category: "aromatic alcohol",
				description: "Aromatic compound with hydroxyl group",
			},
		},
		{
			id: nodeIds.aceticAcid,
			graphElementId: graphElementIds.aceticAcid,
			name: "Acetic Acid",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "CH₃COOH",
				molecularWeight: 60.05,
				category: "carboxylic acid",
				description: "Vinegar component",
			},
		},
		{
			id: nodeIds.formaldehyde,
			graphElementId: graphElementIds.formaldehyde,
			name: "Formaldehyde",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "HCHO",
				molecularWeight: 30.03,
				category: "aldehyde",
				description: "Simplest aldehyde, preservative",
			},
		},
		{
			id: nodeIds.acetone,
			graphElementId: graphElementIds.acetone,
			name: "Acetone",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "CH₃COCH₃",
				molecularWeight: 58.08,
				category: "ketone",
				description: "Common solvent",
			},
		},
		{
			id: nodeIds.benzene,
			graphElementId: graphElementIds.benzene,
			name: "Benzene",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "C₆H₆",
				molecularWeight: 78.11,
				category: "aromatic",
				description: "Simplest aromatic hydrocarbon",
			},
		},
		{
			id: nodeIds.toluene,
			graphElementId: graphElementIds.toluene,
			name: "Toluene",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "C₇H₈",
				molecularWeight: 92.14,
				category: "aromatic",
				description: "Methylbenzene, common solvent",
			},
		},
		{
			id: nodeIds.aniline,
			graphElementId: graphElementIds.aniline,
			name: "Aniline",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "C₆H₅NH₂",
				molecularWeight: 93.13,
				category: "aromatic amine",
				description: "Aminobenzene, used in dyes",
			},
		},
		{
			id: nodeIds.ethene,
			graphElementId: graphElementIds.ethene,
			name: "Ethene",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "C₂H₄",
				molecularWeight: 28.05,
				category: "alkene",
				description: "Ethylene, simplest alkene",
			},
		},
		{
			id: nodeIds.ethyne,
			graphElementId: graphElementIds.ethyne,
			name: "Ethyne",
			elementType: "compound",
			typeId: null,
			metadata: {
				formula: "C₂H₂",
				molecularWeight: 26.04,
				category: "alkyne",
				description: "Acetylene, used in welding",
			},
		},
		// Functional Groups
		{
			id: nodeIds.hydroxyl,
			graphElementId: graphElementIds.hydroxyl,
			name: "Hydroxyl Group",
			elementType: "functional_group",
			typeId: null,
			metadata: {
				formula: "-OH",
				description: "Alcohol and phenol functional group",
			},
		},
		{
			id: nodeIds.carbonyl,
			graphElementId: graphElementIds.carbonyl,
			name: "Carbonyl Group",
			elementType: "functional_group",
			typeId: null,
			metadata: {
				formula: "C=O",
				description: "Found in aldehydes, ketones, and carboxylic acids",
			},
		},
		{
			id: nodeIds.carboxyl,
			graphElementId: graphElementIds.carboxyl,
			name: "Carboxyl Group",
			elementType: "functional_group",
			typeId: null,
			metadata: {
				formula: "-COOH",
				description: "Carboxylic acid functional group",
			},
		},
		{
			id: nodeIds.amino,
			graphElementId: graphElementIds.amino,
			name: "Amino Group",
			elementType: "functional_group",
			typeId: null,
			metadata: {
				formula: "-NH₂",
				description: "Amine functional group",
			},
		},
		{
			id: nodeIds.methyl,
			graphElementId: graphElementIds.methyl,
			name: "Methyl Group",
			elementType: "functional_group",
			typeId: null,
			metadata: {
				formula: "-CH₃",
				description: "Simplest alkyl group",
			},
		},
		{
			id: nodeIds.ethyl,
			graphElementId: graphElementIds.ethyl,
			name: "Ethyl Group",
			elementType: "functional_group",
			typeId: null,
			metadata: {
				formula: "-C₂H₅",
				description: "Ethyl alkyl group",
			},
		},
		{
			id: nodeIds.benzyl,
			graphElementId: graphElementIds.benzyl,
			name: "Benzyl Group",
			elementType: "functional_group",
			typeId: null,
			metadata: {
				formula: "-C₆H₅CH₂",
				description: "Benzyl substituent",
			},
		},
		// Properties
		{
			id: nodeIds.flammable,
			graphElementId: graphElementIds.flammable,
			name: "Flammable",
			elementType: "property",
			typeId: null,
			metadata: {
				description: "Capable of catching fire easily",
			},
		},
		{
			id: nodeIds.polar,
			graphElementId: graphElementIds.polar,
			name: "Polar",
			elementType: "property",
			typeId: null,
			metadata: {
				description: "Has a dipole moment due to uneven charge distribution",
			},
		},
		{
			id: nodeIds.volatile,
			graphElementId: graphElementIds.volatile,
			name: "Volatile",
			elementType: "property",
			typeId: null,
			metadata: {
				description: "Evaporates easily at room temperature",
			},
		},
		{
			id: nodeIds.soluble,
			graphElementId: graphElementIds.soluble,
			name: "Water Soluble",
			elementType: "property",
			typeId: null,
			metadata: {
				description: "Dissolves in water",
			},
		},
	];

	// Predicate IDs for edges
	const predicateIds = {
		hasFunctionalGroup: generateMockId("predicate-hasFunctionalGroup"),
		hasProperty: generateMockId("predicate-hasProperty"),
		reactsWith: generateMockId("predicate-reactsWith"),
		derivedFrom: generateMockId("predicate-derivedFrom"),
	};

	const edges = [
		// Functional groups
		{
			id: generateMockId("edge-methanol-hydroxyl"),
			predicateId: predicateIds.hasFunctionalGroup,
			source: nodeIds.methanol,
			target: nodeIds.hydroxyl,
			tuplePosition: 0,
			label: "hasFunctionalGroup",
		},
		{
			id: generateMockId("edge-ethanol-hydroxyl"),
			predicateId: predicateIds.hasFunctionalGroup,
			source: nodeIds.ethanol,
			target: nodeIds.hydroxyl,
			tuplePosition: 0,
			label: "hasFunctionalGroup",
		},
		{
			id: generateMockId("edge-phenol-hydroxyl"),
			predicateId: predicateIds.hasFunctionalGroup,
			source: nodeIds.phenol,
			target: nodeIds.hydroxyl,
			tuplePosition: 0,
			label: "hasFunctionalGroup",
		},
		{
			id: generateMockId("edge-acetic-acid-carboxyl"),
			predicateId: predicateIds.hasFunctionalGroup,
			source: nodeIds.aceticAcid,
			target: nodeIds.carboxyl,
			tuplePosition: 0,
			label: "hasFunctionalGroup",
		},
		{
			id: generateMockId("edge-formaldehyde-carbonyl"),
			predicateId: predicateIds.hasFunctionalGroup,
			source: nodeIds.formaldehyde,
			target: nodeIds.carbonyl,
			tuplePosition: 0,
			label: "hasFunctionalGroup",
		},
		{
			id: generateMockId("edge-acetone-carbonyl"),
			predicateId: predicateIds.hasFunctionalGroup,
			source: nodeIds.acetone,
			target: nodeIds.carbonyl,
			tuplePosition: 0,
			label: "hasFunctionalGroup",
		},
		{
			id: generateMockId("edge-aniline-amino"),
			predicateId: predicateIds.hasFunctionalGroup,
			source: nodeIds.aniline,
			target: nodeIds.amino,
			tuplePosition: 0,
			label: "hasFunctionalGroup",
		},
		{
			id: generateMockId("edge-toluene-methyl"),
			predicateId: predicateIds.hasFunctionalGroup,
			source: nodeIds.toluene,
			target: nodeIds.methyl,
			tuplePosition: 0,
			label: "hasFunctionalGroup",
		},
		// Properties
		{
			id: generateMockId("edge-methane-flammable"),
			predicateId: predicateIds.hasProperty,
			source: nodeIds.methane,
			target: nodeIds.flammable,
			tuplePosition: 0,
			label: "hasProperty",
		},
		{
			id: generateMockId("edge-ethane-flammable"),
			predicateId: predicateIds.hasProperty,
			source: nodeIds.ethane,
			target: nodeIds.flammable,
			tuplePosition: 0,
			label: "hasProperty",
		},
		{
			id: generateMockId("edge-methanol-flammable"),
			predicateId: predicateIds.hasProperty,
			source: nodeIds.methanol,
			target: nodeIds.flammable,
			tuplePosition: 0,
			label: "hasProperty",
		},
		{
			id: generateMockId("edge-methanol-polar"),
			predicateId: predicateIds.hasProperty,
			source: nodeIds.methanol,
			target: nodeIds.polar,
			tuplePosition: 0,
			label: "hasProperty",
		},
		{
			id: generateMockId("edge-methanol-soluble"),
			predicateId: predicateIds.hasProperty,
			source: nodeIds.methanol,
			target: nodeIds.soluble,
			tuplePosition: 0,
			label: "hasProperty",
		},
		{
			id: generateMockId("edge-ethanol-flammable"),
			predicateId: predicateIds.hasProperty,
			source: nodeIds.ethanol,
			target: nodeIds.flammable,
			tuplePosition: 0,
			label: "hasProperty",
		},
		{
			id: generateMockId("edge-ethanol-polar"),
			predicateId: predicateIds.hasProperty,
			source: nodeIds.ethanol,
			target: nodeIds.polar,
			tuplePosition: 0,
			label: "hasProperty",
		},
		{
			id: generateMockId("edge-ethanol-soluble"),
			predicateId: predicateIds.hasProperty,
			source: nodeIds.ethanol,
			target: nodeIds.soluble,
			tuplePosition: 0,
			label: "hasProperty",
		},
		{
			id: generateMockId("edge-acetone-volatile"),
			predicateId: predicateIds.hasProperty,
			source: nodeIds.acetone,
			target: nodeIds.volatile,
			tuplePosition: 0,
			label: "hasProperty",
		},
		{
			id: generateMockId("edge-acetone-polar"),
			predicateId: predicateIds.hasProperty,
			source: nodeIds.acetone,
			target: nodeIds.polar,
			tuplePosition: 0,
			label: "hasProperty",
		},
		{
			id: generateMockId("edge-formaldehyde-volatile"),
			predicateId: predicateIds.hasProperty,
			source: nodeIds.formaldehyde,
			target: nodeIds.volatile,
			tuplePosition: 0,
			label: "hasProperty",
		},
		// Derived from relationships
		{
			id: generateMockId("edge-ethanol-derived-ethane"),
			predicateId: predicateIds.derivedFrom,
			source: nodeIds.ethanol,
			target: nodeIds.ethane,
			tuplePosition: 0,
			label: "derivedFrom",
		},
		{
			id: generateMockId("edge-methanol-derived-methane"),
			predicateId: predicateIds.derivedFrom,
			source: nodeIds.methanol,
			target: nodeIds.methane,
			tuplePosition: 0,
			label: "derivedFrom",
		},
		{
			id: generateMockId("edge-toluene-derived-benzene"),
			predicateId: predicateIds.derivedFrom,
			source: nodeIds.toluene,
			target: nodeIds.benzene,
			tuplePosition: 0,
			label: "derivedFrom",
		},
		{
			id: generateMockId("edge-phenol-derived-benzene"),
			predicateId: predicateIds.derivedFrom,
			source: nodeIds.phenol,
			target: nodeIds.benzene,
			tuplePosition: 0,
			label: "derivedFrom",
		},
		{
			id: generateMockId("edge-aniline-derived-benzene"),
			predicateId: predicateIds.derivedFrom,
			source: nodeIds.aniline,
			target: nodeIds.benzene,
			tuplePosition: 0,
			label: "derivedFrom",
		},
		// Reacts with relationships
		{
			id: generateMockId("edge-ethanol-reacts-acetic"),
			predicateId: predicateIds.reactsWith,
			source: nodeIds.ethanol,
			target: nodeIds.aceticAcid,
			tuplePosition: 0,
			label: "reactsWith",
		},
		{
			id: generateMockId("edge-methanol-reacts-acetic"),
			predicateId: predicateIds.reactsWith,
			source: nodeIds.methanol,
			target: nodeIds.aceticAcid,
			tuplePosition: 0,
			label: "reactsWith",
		},
	];

	const edgesWithTupleId = edges.map((edge) => ({
		...edge,
		tupleId: generateMockId(`tuple:${edge.predicateId}:${edge.source}:${edge.target}:${edge.id}`),
	}));

	return {
		graph: {
			id: graphId,
			name: "Organic Chemistry Compounds",
			description:
				"A knowledge graph of organic chemical compounds, their properties, functional groups, and relationships",
			createdAt: now,
			updatedAt: now,
		},
		nodes,
		edges: edgesWithTupleId,
	};
};
